import React, { useState, useEffect } from 'react';
import { supabase } from '../supabase/config';

const formatDateTime = (dateTimeStr) => {
  const date = new Date(dateTimeStr);
  return date.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  });
};

const calcStatus = (startStr, endStr) => {
  const now = new Date();
  const start = new Date(startStr);
  const end = new Date(endStr);
  if (now >= end) return { color: '#9e9e9e', text: 'Ended' };
  if (now >= start) return { color: '#34a853', text: 'Started' };
  return { color: '#4285f4', text: 'Upcoming' };
};

const GuestEventDetailsPanel = () => {
  const [data, setData] = useState(null);
  // data = { eventData, organizerName, organizerAvatar }
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const handleOpen = async (e) => {
      const eventData = e.detail;

      // Fetch organizer info
      let organizerName = null;
      let organizerAvatar = null;
      if (eventData.creator_id) {
        try {
          const supabaseClient = window.supabase || supabase;
          const { data: user } = await supabaseClient
            .from('users')
            .select('name, profile_picture')
            .eq('id', eventData.creator_id)
            .maybeSingle();
          if (user) {
            organizerName = user.name;
            organizerAvatar = user.profile_picture;
          }
        } catch (e) {
          console.error('Error fetching organizer:', e);
        }
      }

      setData({ eventData, organizerName, organizerAvatar });
      setVisible(true);
    };

    const handleClose = () => {
      setVisible(false);
      setTimeout(() => setData(null), 300);
    };

    window.addEventListener('openGuestEventPanel', handleOpen);
    window.addEventListener('closeGuestEventPanel', handleClose);

    return () => {
      window.removeEventListener('openGuestEventPanel', handleOpen);
      window.removeEventListener('closeGuestEventPanel', handleClose);
    };
  }, []);

  if (!data && !visible) return null;

  const { eventData, organizerName, organizerAvatar } = data || {};
  const status = eventData ? calcStatus(eventData.start_date_time, eventData.end_date_time) : null;
  const participantCount = eventData?.participants?.length || 1;
  const limit = eventData?.people_limit;
  const limitText = limit ? `${participantCount} / ${limit}` : `${participantCount} / ∞`;
  const photoUrl = eventData?.place_photo_url || eventData?.place_icon || '';
  const fallbackAvatar = `https://ui-avatars.com/api/?name=${encodeURIComponent(organizerName || 'User')}&background=e8eaed&color=5f6368&size=48`;

  const handleClose = () => {
    setVisible(false);
    setTimeout(() => setData(null), 300);
  };

  const handleSignUp = () => {
    handleClose();
    if (window.cleanupGuestMap) window.cleanupGuestMap();
    window.location.href = window.location.pathname;
  };

  if (!eventData) return null;

  return (
    <>
      <style>{`
        .guest-event-panel {
          position: fixed;
          top: 65px;
          right: -420px;
          width: 380px;
          max-width: 100vw;
          height: calc(100vh - 65px);
          background: white;
          box-shadow: -4px 0 20px rgba(0,0,0,0.08);
          z-index: 10001;
          overflow: hidden;
          font-family: 'Outfit', sans-serif;
          transition: right 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          display: flex;
          flex-direction: column;
        }
        .guest-event-panel.panel-visible {
          right: 0;
        }
        @media (max-width: 640px) {
          .guest-event-panel {
            width: 100vw !important;
            right: -100vw !important;
            top: 50px !important;
            height: calc(100vh - 50px - 42px) !important;
          }
          .guest-event-panel.panel-visible {
            right: 0 !important;
          }
        }
      `}</style>
    <div
      className={`guest-event-panel${visible ? ' panel-visible' : ''}`}
      style={{ borderLeft: `3px solid ${status.color}` }}
    >
      {/* Scrollable content */}
      <div style={{ flex: 1, overflowY: 'auto', WebkitOverflowScrolling: 'touch', padding: '20px' }}>

        {/* Close button */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', float: 'right' }}>
          <button
            onClick={handleClose}
            onMouseOver={(e) => e.currentTarget.style.background = '#f1f3f4'}
            onMouseOut={(e) => e.currentTarget.style.background = 'none'}
            title="Close"
            style={{ background: 'none', border: 'none', color: '#5f6368', cursor: 'pointer', padding: 0, width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '50%', transition: 'background 0.2s' }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px', paddingRight: '40px' }}>
          <h2 style={{ margin: 0, fontSize: '24px', fontWeight: 500, color: '#202124' }}>Event Details</h2>
          {photoUrl && <img src={photoUrl} style={{ width: '28px', height: '28px', borderRadius: '6px', objectFit: 'cover' }} alt="" />}
        </div>

        {/* Event card */}
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', padding: '12px', background: '#f8f9fa', borderRadius: '12px', border: `2px solid ${status.color}`, boxShadow: '0 4px 12px rgba(0,0,0,0.1)', marginBottom: '20px' }}>
          {photoUrl ? (
            <img src={photoUrl} alt={eventData.place_name} style={{ width: '48px', height: '48px', borderRadius: '8px', objectFit: 'cover', flexShrink: 0 }} />
          ) : (
            <div style={{ width: '48px', height: '48px', borderRadius: '8px', background: '#e8eaed', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="#9ca3af">
                <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" />
              </svg>
            </div>
          )}
          <div style={{ flex: 1, minWidth: 0 }}>
            <h3 style={{ margin: '0 0 4px', fontSize: '16px', fontWeight: 600, color: '#202124', lineHeight: 1.3 }}>
              {eventData.place_name}
            </h3>
            <p style={{ margin: 0, fontSize: '12px', color: '#5f6368', display: 'flex', alignItems: 'center', gap: '4px' }}>
              <span style={{ color: '#ea4335' }}>📍</span>
              {eventData.place_address || `${eventData.latitude?.toFixed(4)}, ${eventData.longitude?.toFixed(4)}`}
            </p>
          </div>
        </div>

        {/* Title */}
        <div style={{ marginBottom: '16px' }}>
          <p style={{ margin: 0, fontSize: '20px', fontWeight: 600, color: '#34a853' }}>{eventData.title}</p>
        </div>

        {/* Description */}
        {eventData.description && (
          <div style={{ marginBottom: '20px' }}>
            <p style={{ margin: '0 0 4px', fontSize: '11px', color: '#5f6368', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Description</p>
            <p style={{ margin: 0, fontSize: '14px', color: '#202124', lineHeight: 1.5 }}>{eventData.description}</p>
          </div>
        )}

        {/* Time */}
        <div style={{ background: '#f8f9fa', borderRadius: '12px', padding: '16px', marginBottom: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
            <div style={{ width: '36px', height: '36px', background: '#e8f5e9', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#34a853" strokeWidth="2">
                <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
              </svg>
            </div>
            <div>
              <p style={{ margin: 0, fontSize: '11px', color: '#5f6368', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Starts</p>
              <p style={{ margin: 0, fontSize: '14px', color: '#202124', fontWeight: 500 }}>{formatDateTime(eventData.start_date_time)}</p>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{ width: '36px', height: '36px', background: '#fce8e6', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#ea4335" strokeWidth="2">
                <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
              </svg>
            </div>
            <div>
              <p style={{ margin: 0, fontSize: '11px', color: '#5f6368', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Ends</p>
              <p style={{ margin: 0, fontSize: '14px', color: '#202124', fontWeight: 500 }}>{formatDateTime(eventData.end_date_time)}</p>
            </div>
          </div>
        </div>

        {/* Status */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
          <span style={{ width: '10px', height: '10px', background: status.color, borderRadius: '50%' }} />
          <span style={{ fontSize: '14px', fontWeight: 500, color: status.color }}>{status.text}</span>
        </div>

        {/* Participants */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', background: '#f8f9fa', borderRadius: '12px', marginBottom: '16px' }}>
          <span style={{ fontSize: '11px', color: '#5f6368', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Participants</span>
          <span style={{ fontSize: '16px', fontWeight: 600, color: '#202124' }}>{limitText}</span>
        </div>

        {/* Organizer */}
        {organizerName && (
          <div style={{ marginBottom: '20px' }}>
            <p style={{ margin: '0 0 8px', fontSize: '11px', color: '#5f6368', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Organized by</p>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <img
                src={organizerAvatar || fallbackAvatar}
                alt={organizerName}
                style={{ width: '40px', height: '40px', borderRadius: '50%', objectFit: 'cover', border: '2px solid #e0e0e0' }}
                onError={(e) => { e.target.src = fallbackAvatar; }}
              />
              <span style={{ fontSize: '14px', fontWeight: 500, color: '#202124' }}>{organizerName}</span>
            </div>
          </div>
        )}
      </div>

      {/* Fixed CTA */}
      <div style={{ padding: '16px 20px', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', flexShrink: 0 }}>
        <p style={{ margin: '0 0 4px', fontSize: '15px', fontWeight: 500, color: 'white', textAlign: 'center' }}>Want to join this event?</p>
        <p style={{ margin: '0 0 12px', fontSize: '13px', color: 'rgba(255,255,255,0.8)', textAlign: 'center' }}>Create a free account to participate</p>
        <button
          onClick={handleSignUp}
          onMouseOver={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)'; }}
          onMouseOut={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }}
          style={{ width: '100%', background: 'white', color: '#667eea', border: 'none', padding: '14px 32px', borderRadius: '12px', fontSize: '15px', fontWeight: 600, cursor: 'pointer', transition: 'all 0.2s', fontFamily: "'Outfit', sans-serif" }}
        >
          Sign Up Free
        </button>
      </div>
    </div>
    </>
  );
};

export default GuestEventDetailsPanel;